import { cn } from '@/lib/utils';
import { forwardRef, useCallback } from 'react';
import InlineTextInput, {
  type InlineTextInputProps,
} from './inline-text-input';

interface CurrencyInputProps
  extends Omit<InlineTextInputProps, 'startAdornment'> {
  // Additional props specific to CurrencyInput can go here
  weight?: 'regular' | 'medium' | 'bold';
  wrapperClassName?: string;
  startAdornmentClassName?: string;
  tabIndex?: number;
}

export const formatWithCents = (value: string) => {
  if (!value.includes('.')) {
    return `${value}.00`;
  }

  if (value.split('.')[1]?.length < 2) {
    return `${value}0`;
  }

  return value;
};

export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  (props, ref) => {
    const {
      placeholder,
      value,
      onChange,
      wrapperClassName,
      tabIndex,
      startAdornmentClassName,
      ...rest
    } = props;

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value;

        const formattedValue = rawValue;

        // Creates a new synthetic event with the formatted value
        // TODO (jimmy): fix the synthetic event so that the cursor position doesn't change
        // when a user types a number passed the decimal point when it already has a 2 decimal places
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: formattedValue,
          },
        };

        // If there are more than 2 "."s, return
        if (rawValue.split('.')?.length > 2 && value) {
          return;
        }

        // If there are more than 2 decimal places, return
        if (rawValue.split('.')[1]?.length > 2 && value) {
          return;
        }

        // onChange?.(e);
        onChange?.(syntheticEvent);
      },
      [value, onChange],
    );

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;

      const formattedValue = formatWithCents(rawValue);

      onChange?.({ ...e, target: { ...e.target, value: formattedValue } });
    };

    return (
      <InlineTextInput
        tabIndex={tabIndex}
        // id="currency"
        value={value}
        onChange={handleChange}
        // onChange={onChange}
        type="number"
        min={0}
        max={1000000}
        step={0.01}
        placeholder={placeholder}
        startAdornment={
          <span
            className={cn(
              'text-gray-500',
              {
                'text-uneditable': props.readOnly,
              },
              startAdornmentClassName,
            )}
          >
            $
          </span>
        }
        className={cn(rest.className, {
          'text-uneditable': props.readOnly,
        })}
        ref={ref}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        wrapperClassName={cn('w-fit', wrapperClassName)}
        {...rest}
      />
    );
  },
);

CurrencyInput.displayName = 'CurrencyInput';
